
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function InvestmentThesis() {
  const successCases = [
    {
      company: "Belvo",
      route: "Spain → Mexico, Brazil",
      summary: "Barcelona-based open-finance API provider expanded into Brazil and Mexico early, becoming a backbone for banks and fintechs across the region.",
      impact: "Serves 150+ customers, including BBVA, Santander and Mercado Libre in Mexico/Brazil; scaled platform coverage and fundraising to fuel LATAM growth.",
      source: "https://belvo.com/blog/belvo-secures-15-million-in-new-round-of-funding-open-finance-latin-america/?utm_source=chatgpt.com"
    },
    {
      company: "Cabify",
      route: "Spain — Large LATAM footprint",
      summary: "Madrid origin ride-hailing company scaled aggressively in Latin America rather than focusing solely on Europe — building a multi-country transport network and logistics footprint across the region.",
      impact: "Operating in 40+ Latin American cities and facilitating tens of millions of rides (reporting ~91M rides and ~76M users in a recent period). This regional scale made Cabify a leading mobility player in LATAM.",
      source: "https://www.worldbenchmarkingalliance.org/publication/urban/companies/cabify/?utm_source=chatgpt.com"
    },
    {
      company: "Glovo",
      route: "Spain → LATAM operations & impact",
      summary: "Barcelona-founded on-demand delivery platform used LATAM as a major growth theatre (and later strategic exits/partnerships) — generating significant local GMV and partner revenue.",
      impact: "Operating across many countries and cities (1,300+ cities globally at peak) and credited with driving €billions in partner revenue; LATAM was a meaningful part of that expansion story.",
      source: "https://about.glovoapp.com/this-is-glovo/?utm_source=chatgpt.com"
    },
    {
      company: "Mambu",
      route: "Germany → LATAM customers & partners",
      summary: "Berlin-based cloud banking platform won early LATAM clients and enabled local fintech scale-ups (e.g., MACH in Chile), providing the modular core banking stack needed for fast launches.",
      impact: "Mambu-powered clients (like MACH) reached millions of users (MACH reporting 3M+ users), demonstrating how a European SaaS banking stack helped local LATAM fintechs scale fast.",
      source: "https://www.fintechfutures.com/fintech/mambu-signs-first-brazilian-client-acesso?utm_source=chatgpt.com"
    },
    {
      company: "Wise / TransferWise",
      route: "UK → Brazil & broader LATAM",
      summary: "Wise (formerly TransferWise) expanded payment rails and local capabilities in Brazil and other LATAM markets to capture cross-border remittance and payments share.",
      impact: "Wise moved £100s of billions and has millions of customers globally; in Brazil it captured a meaningful share of cross-border flows by partnering with local banks and payment methods (e.g., Boleto), showing that European payments stacks can win with the right local execution.",
      source: "https://wise.com/us/blog/brazil-welcome-to-transferwise?utm_source=chatgpt.com"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
              Investment Thesis
            </h1>
            <p className="text-lg md:text-2xl text-slate-200 leading-relaxed">
              Bridging European innovation with Latin American market opportunities through strategic capital and operational expertise.
            </p>
          </div>
        </div>
      </section>

      {/* Our Investment Focus (formerly Key Stats) */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 text-center" style={{ fontFamily: '"Playfair Display", serif' }}>
            Our Investment Focus
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-2xl md:text-4xl font-bold text-slate-900 mb-2">€50k-€150k</div>
              <div className="text-sm md:text-base text-slate-600">Investment Range</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-4xl font-bold text-slate-900 mb-2">3-6 months</div>
              <div className="text-sm md:text-base text-slate-600">Market Entry Timeline</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-4xl font-bold text-slate-900 mb-2">30+ years</div>
              <div className="text-sm md:text-base text-slate-600">LATAM Experience</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-4xl font-bold text-slate-900 mb-2">5 countries</div>
              <div className="text-sm md:text-base text-slate-600">LATAM Coverage</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          
          {/* Problem Section */}
          <div className="mb-20">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mr-4">
                <i className="ri-error-warning-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-slate-900">The Problem</h2>
            </div>
            <div className="bg-gradient-to-r from-red-50 to-orange-50 p-8 rounded-xl border-l-4 border-red-500">
              <p className="text-base md:text-lg text-slate-700 mb-4 leading-relaxed">
                Despite the opportunity, many European pre-seed/seed companies struggle to enter LATAM efficiently.
              </p>
              <p className="text-base md:text-lg text-slate-700 mb-6 leading-relaxed font-semibold">
                4 Key friction points we observe:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h4 className="font-semibold text-slate-900 mb-3">Cultural Complexity</h4>
                  <p className="text-sm md:text-base text-slate-600">Underestimating cultural, operational and regulatory complexity despite language affinities (Iberian founders).</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h4 className="font-semibold text-slate-900 mb-3">Talent Cost Surprises</h4>
                  <p className="text-sm md:text-base text-slate-600">Hiring seasoned leaders locally in LATAM can cost as much or more than in Europe.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h4 className="font-semibold text-slate-900 mb-3">Regulatory Hurdles</h4>
                  <p className="text-sm md:text-base text-slate-600">Foreign-founded startups may face local hiring quotas, public funding conditions, and slow approvals.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h4 className="font-semibold text-slate-900 mb-3">Execution Gaps</h4>
                  <p className="text-sm md:text-base text-slate-600">Market entry often delayed or mis-executed due to weak local execution.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Opportunity Section */}
          <div className="mb-20">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                <i className="ri-line-chart-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-slate-900">The Opportunity</h2>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-xl border-l-4 border-green-500">
              <p className="text-base md:text-lg text-slate-700 mb-6 leading-relaxed">
                Latin America presents a large, under-served, high-growth market for tech startups. The convergence of European expansion appetite, LATAM's startup momentum, and the critical need for local expertise creates an unprecedented opportunity for founders who can navigate these markets effectively.
              </p>
              
              {/* Data Points */}
              <div className="space-y-6 mb-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-blue-500">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <i className="ri-bar-chart-line text-white text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm md:text-base text-slate-700 mb-2">
                        <strong>63% of Spanish companies say they will increase investment in LATAM in 2025</strong> — demonstrating strong European commitment to the region.
                      </p>
                      <p className="text-xs md:text-sm text-slate-500">
                        Source: <a href="https://www.ie.edu/university/news-events/news/more-60-spanish-companies-plan-increase-investment-latin-america-2025/?utm_source=chatgpt.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline cursor-pointer">IE University / Auxadi, 2025</a>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-purple-500">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <i className="ri-rocket-line text-white text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm md:text-base text-slate-700 mb-2">
                        <strong>Nearly 500 startups in Latin America raised their first VC round in the past 18 months</strong> — emphasising the region's early-stage momentum and growing investor confidence.
                      </p>
                      <p className="text-xs md:text-sm text-slate-500">
                        Source: <a href="https://www.lavca.org/research/2025-latin-american-startup-ecosystem-insights/?utm_source=chatgpt.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline cursor-pointer">LAVCA 2025</a>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-orange-500">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <i className="ri-alert-line text-white text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm md:text-base text-slate-700 mb-2">
                        <strong>European founders are looking at Latin America as a high-potential market, but frequently cite local execution, regulation, hiring and payment-rail issues as major barriers</strong> — highlighting the exact gap EULA VC fills.
                      </p>
                      <p className="text-xs md:text-sm text-slate-500">
                        Source: <a href="https://sifted.eu/articles/europe-startups-latin-america-markets?utm_source=chatgpt.com" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline cursor-pointer">Sifted, Jan 2024</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-emerald-600 text-white p-6 rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center">
                  <i className="ri-lightbulb-line mr-2"></i>
                  Why LATAM-Tailored Senior Expertise Matters
                </h4>
                <p className="text-sm md:text-base leading-relaxed">
                  European founders possess strong product foundations and technical capabilities, but LATAM's unique regulatory landscape, cultural nuances, payment infrastructure, and talent market require specialized local expertise. With hundreds of millions of underserved customers and a rapidly maturing startup ecosystem, the region offers massive scale potential — but only for those who can execute locally with precision and cultural intelligence.
                </p>
              </div>
            </div>
          </div>

          {/* Our Value Proposition (formerly Value-Add Section) */}
          <div className="mb-20">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                <i className="ri-star-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-slate-900">Our Value Proposition</h2>
            </div>
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 text-slate-800 p-8 rounded-xl mb-8">
              <p className="text-base md:text-lg leading-relaxed mb-6">
                At EULA VC, we pair capital with hands‑on, senior operational and financial leadership rooted in decades of banking and management excellence across Latin America. With over 45 years of combined executive experience across Brazil, Mexico, Argentina, Chile, and Colombia, our team brings more than funding — we bring execution.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-slate-700">
                Following a successful Pitch Us submission and diagnostic assessment, each selected startup is strategically aligned with our network of seasoned CEOs, local business managers, and sector leaders. From Fintech and PropTech to AgriTech, Energy, and Healthcare, successful founders gain direct access to on‑the‑ground executives who act as trusted external counsel, helping navigate regional dynamics and accelerate expansion.
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-8 mb-8">
              <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 flex items-center">
                <i className="ri-rocket-line mr-3 text-blue-600"></i>
                Through this model, EULA VC helps startups:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <i className="ri-shield-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Navigate Regulatory Complexity</h4>
                      <p className="text-sm md:text-base text-slate-700">Establish local compliance frameworks and navigate the intricate regulatory landscape across LATAM markets.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <i className="ri-line-chart-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Optimize Unit Economics</h4>
                      <p className="text-sm md:text-base text-slate-700">Tailor pricing strategies and FX optimization for each market to maximize profitability and growth potential.</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <i className="ri-team-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Build Strong Local Leadership</h4>
                      <p className="text-sm md:text-base text-slate-700">Develop robust local leadership teams and organizational structures tailored to regional market dynamics.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <i className="ri-speed-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Accelerate Go‑to‑Market</h4>
                      <p className="text-sm md:text-base text-slate-700">Leverage deep relationships with LATAM corporates, financial institutions, and regulators for rapid market penetration.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-slate-100 to-slate-200 text-slate-800 p-8 rounded-xl">
              <h3 className="text-lg md:text-xl font-semibold mb-4 flex items-center">
                <i className="ri-handshake-line mr-3 text-slate-600"></i>
                Our Commitment
              </h3>
              <p className="text-base md:text-lg leading-relaxed text-slate-700">
                We invest, we embed, and we execute alongside you — transforming European innovation into Latin American scale.
              </p>
            </div>
          </div>

          {/* Target Investments Section (renamed to Target Sectors) */}
          <div className="mb-20">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mr-4">
                <i className="ri-focus-3-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-slate-900">Target Sectors</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-3">Company Profile</h4>
                <p className="text-sm md:text-base text-purple-700">European pre‑seed & seed tech companies with product‑market fit and explicit intent to scale into LATAM.</p>
              </div>
              <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-3">Investment Size</h4>
                <p className="text-sm md:text-base text-purple-700">€50k–€300k for initial seed, with optional follow‑on support for standout winners.</p>
              </div>
              <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-3">Selection Criteria</h4>
                <p className="text-sm md:text-base text-purple-700">Founder commitment to LATAM expansion, scalable growth potential, amenable to operational oversight.</p>
              </div>
            </div>
          </div>

          {/* Deal Structure Section (renamed to Investment Criteria) */}
          <div className="mb-20">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center mr-4">
                <i className="ri-file-text-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-slate-900">Investment Criteria</h2>
            </div>
            
            <div className="bg-indigo-50 p-8 rounded-xl mb-8">
              <p className="text-base md:text-lg text-indigo-900 mb-4 leading-relaxed">
                <strong>Our Philosophy:</strong> We believe in tailored partnerships that match founder needs with appropriate capital and support intensity. Our three‑tier structure allows us to work with:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="bg-white p-4 rounded-lg">
                  <div className="text-xl md:text-2xl font-bold text-indigo-900 mb-1">3‑4</div>
                  <div className="text-sm md:text-base text-indigo-700">Light Tier Companies</div>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <div className="text-xl md:text-2xl font-bold text-indigo-900 mb-1">2‑3</div>
                  <div className="text-sm md:text-base text-indigo-700">Standard Tier Companies</div>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <div className="text-xl md:text-2xl font-bold text-indigo-900 mb-1">1‑2</div>
                  <div className="text-sm md:text-base text-indigo-700">Premium Tier Companies</div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              {/* Light Tier */}
              <div className="bg-white border-2 border-green-200 rounded-xl p-8">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-seedling-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-slate-900">LIGHT TIER</h3>
                    <p className="text-sm md:text-base text-green-600 font-medium">Pre‑Seed / Idea‑Stage</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                  <div className="text-center">
                    <div className="text-lg md:text-xl font-bold text-slate-900 mb-1">€50K – €75K</div>
                    <div className="text-xs md:text-sm text-slate-600">Investment Capital</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg md:text-xl font-bold text-slate-900 mb-1">5 – 6%</div>
                    <div className="text-xs md:text-sm text-slate-600">Equity Stake</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg md:text-xl font-bold text-slate-900 mb-1">3 months</div>
                    <div className="text-xs md:text-sm text-slate-600">Duration</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg md:text-xl font-bold text-slate-900 mb-1">3‑4</div>
                    <div className="text-xs md:text-sm text-slate-600">Companies/Year</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
                      <i className="ri-flag-line mr-2 text-green-600"></i>
                      Milestone Triggers
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-slate-700">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Validation of business model
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        MVP completion or LATAM market readiness report
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
                      <i className="ri-service-line mr-2 text-green-600"></i>
                      Core Support Services
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-slate-700">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Access to EULA VC's LATAM Diagnostic Engine
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Founder coaching on LATAM strategy & regulations
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Financial structure review
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Introduction to key local partners (banks, advisors, legal)
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Standard Tier */}
              <div className="bg-white border-2 border-blue-200 rounded-xl p-8">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-plant-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-slate-900">STANDARD TIER</h3>
                    <p className="text-sm md:text-base text-blue-600 font-medium">Seed / Early‑Stage</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                  <div className="text-center">
                    <div className="text-lg md:text-xl font-bold text-slate-900 mb-1">€100K – €150K</div>
                    <div className="text-xs md:text-sm text-slate-600">Investment Capital</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg md:text-xl font-bold text-slate-900 mb-1">7 – 8%</div>
                    <div className="text-xs md:text-sm text-slate-600">Equity Stake</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg md:text-xl font-bold text-slate-900 mb-1">4–5 months</div>
                    <div className="text-xs md:text-sm text-slate-600">Duration</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg md:text-xl font-bold text-slate-900 mb-1">2‑3</div>
                    <div className="text-xs md:text-sm text-slate-600">Companies/Year</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
                      <i className="ri-flag-line mr-2 text-blue-600"></i>
                      Milestone Triggers
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-slate-700">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Local pilot launch in 1 LATAM market
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Local entity or distribution setup
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        3‑month operational KPI targets achieved
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
                      <i className="ri-service-line mr-2 text-blue-600"></i>
                      Core Support Services
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-slate-700">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Full front‑to‑back EULA VC Diagnostic
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Market entry strategy & regulatory navigation
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Introductions to local talent & partners
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Financial modelling & unit‑economics support
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Premium Tier */}
              <div className="bg-white border-2 border-purple-200 rounded-xl p-8">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-cup-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-slate-900">PREMIUM TIER</h3>
                    <p className="text-sm md:text-base text-purple-600 font-medium">Growth / Scale‑Stage</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                  <div className="text-center">
                    <div className="text-lg md:text-xl font-bold text-slate-900 mb-1">€200K – €300K</div>
                    <div className="text-xs md:text-sm text-slate-600">Investment Capital</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg md:text-xl font-bold text-slate-900 mb-1">10 – 12%</div>
                    <div className="text-xs md:text-sm text-slate-600">Equity Stake</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg md:text-xl font-bold text-slate-900 mb-1">6–9 months</div>
                    <div className="text-xs md:text-sm text-slate-600">Duration</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg md:text-xl font-bold text-slate-900 mb-1">1‑2</div>
                    <div className="text-xs md:text-sm text-slate-600">Companies/Year</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
                      <i className="ri-flag-line mr-2 text-purple-600"></i>
                      Milestone Triggers
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-slate-700">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Achieve 5‑10M ARR in target LATAM market
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Secure Series A round with strategic LPs
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
                      <i className="ri-service-line mr-2 text-purple-600"></i>
                      Core Support Services
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-slate-700">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Dedicated senior operating partner (30‑hours/week)
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Board seat / governance assistance
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Full‑scale go‑to‑market execution & channel partnerships
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        Follow‑on capital & exit strategy planning
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Why It Works Section */}
          <div className="mb-16">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center mr-4">
                <i className="ri-lightbulb-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-slate-900">Why It Works</h2>
            </div>
            <div className="bg-gradient-to-r from-indigo-900 to-purple-900 text-white p-8 rounded-xl">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <h4 className="font-semibold mb-3 flex items-center">
                    <i className="ri-puzzle-line mr-2"></i>
                    Perfect Fit
                  </h4>
                  <p className="text-sm md:text-base text-indigo-100">European startups often bring strong product/technology foundations but lack local LATAM market execution muscle — our expertise bridges that gap.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-3 flex items-center">
                    <i className="ri-rocket-line mr-2"></i>
                    Market Tailwinds
                  </h4>
                  <p className="text-sm md:text-base text-indigo-100">LATAM's high growth potential, large size (e.g., Colombia's market comparable to Spain) and rising tech innovation create favourable conditions. <a href="https://sifted.eu/articles/european-techs-2024-in-data?utm_source=chatgpt.com" target="_blank" rel="noopener noreferrer" className="text-indigo-200 hover:text-white underline cursor-pointer">Source</a></p>
                </div>
                <div>
                  <h4 className="font-semibold mb-3 flex items-center">
                    <i className="ri-team-line mr-2"></i>
                    Proven Model
                  </h4>
                  <p className="text-sm md:text-base text-indigo-100">The hands‑on model of "capital + strategic partner + operational execution" reduces risk, speeds up time to market, and enhances exit potential.</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section (updated) */}
          <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to Scale Your Vision?</h2>
              <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
                If you're a European founder with product‑market fit and ambitions to expand into Latin America, we'd love to discuss how our expertise can accelerate your growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/pitch"
                  className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base md:text-lg font-medium rounded-lg text-slate-900 bg-white hover:bg-slate-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-line mr-2"></i>
                  Submit Your Pitch
                </a>
                <a
                  href="mailto:eu.la.vccompany@gmail.com"
                  className="inline-flex items-center justify-center px-8 py-4 border border-white text-base md:text-lg font-medium rounded-lg text-white bg-transparent hover:bg-white hover:text-slate-900 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line mr-2"></i>
                  Contact Us
                </a>
              </div>
            </div>
          </section>

          <Footer />
        </div>
      </section>
    </div>
  );
}
